import { Array, pipe } from 'effect';
import { isArray } from 'effect/Array';
import { z } from 'zod';
import { CheckoutReductionSchema } from '../../Models/Reduction/CheckoutReduction';
import { CheckoutReductionReadLegacyService } from '../Reduction/CheckoutReductionLegacyReadService';
const convertAdjustmentToCheckoutReduction = (adjustment) => {
    const { id, rule: { value: amount, type: ruleType }, is_disabled: isDisabled, starts_at: validFrom, ends_at: validUntil, metadata } = adjustment.discount;
    return CheckoutReductionSchema.parse({
        ...adjustment.metadata,
        ...(typeof metadata.adjustmentData === 'object'
            ? metadata.adjustmentData
            : {}),
        amount,
        amountType: ruleType === 'percentage' ? 'PERCENT' : 'FIXED',
        id,
        isDisabled,
        validFrom,
        validUntil
    });
};
const ReductionSchema = (medusaAdjustment) => z
    .object({
    metadata: z.object({
        type: z.literal('ADJUSTMENT')
    }),
    amount: z.coerce.number()
})
    .passthrough()
    .transform((data) => ({
    ...data,
    ...medusaAdjustment,
    type: 'Reduction',
    reduction: convertAdjustmentToCheckoutReduction(medusaAdjustment)
}));
const LegacyReductionSchema = (medusaAdjustment) => z
    .object({
    metadata: z.object({
        reductionsBreakdown: z.array(z.any())
    })
})
    .passthrough()
    .transform((data) => {
    const reductions = CheckoutReductionReadLegacyService.extractReductionFromAdjustment(medusaAdjustment);
    return reductions.map((reduction) => ({
        ...data,
        ...medusaAdjustment,
        //TODO: bad amount, recalculated in LineItemParseService
        type: 'Reduction',
        reduction
    }));
});
const PromoCodeSchema = (medusaAdjustment) => z
    .object({
    metadata: z
        .nullable(z.object({ type: z.undefined() }).partial())
        .default({}),
    amount: z.coerce.number()
})
    .passthrough()
    .transform((data) => ({
    ...data,
    ...medusaAdjustment,
    type: 'PromoCode'
}));
const isAutoDiscount = (adjustment) => {
    var _a;
    return ((_a = adjustment.metadata) === null || _a === void 0 ? void 0 : _a.type) === 'AUTO_DISCOUNT' &&
        Array.isArray(adjustment.metadata.attendeeIds);
};
const isInsurance = (adjustment) => { var _a; return ((_a = adjustment.metadata) === null || _a === void 0 ? void 0 : _a.type) === 'INSURANCE'; };
const AdjustmentSchema = (medusaAdjustment) => ReductionSchema(medusaAdjustment)
    .or(LegacyReductionSchema(medusaAdjustment))
    .or(PromoCodeSchema(medusaAdjustment))
    .parse(medusaAdjustment);
const parseAdjustments = (adjustments) => pipe(adjustments, Array.reduce({
    adjustments: [],
    insurance: null,
    autoDiscountByAttendee: {}
}, (acc, adjustment) => {
    var _a;
    if (isAutoDiscount(adjustment)) {
        const { attendeeIds } = adjustment.metadata;
        const attendeesCount = attendeeIds.length;
        for (const attendeeId of attendeeIds) {
            acc.autoDiscountByAttendee[attendeeId] = Math.round(((_a = acc.autoDiscountByAttendee[attendeeId]) !== null && _a !== void 0 ? _a : 0) +
                adjustment.amount / attendeesCount);
        }
        return acc;
    }
    if (isInsurance(adjustment)) {
        if (acc.insurance) {
            throw new Error('Multiple insurance adjustments found');
        }
        return {
            ...acc,
            insurance: {
                amount: 
                // Insurance is a negative adjustment
                adjustment.amount > 0 ? -adjustment.amount : adjustment.amount,
                code: adjustment.discount.code
            }
        };
    }
    const adjustmentParsed = AdjustmentSchema(adjustment);
    return {
        ...acc,
        adjustments: [
            ...acc.adjustments,
            ...(isArray(adjustmentParsed)
                ? adjustmentParsed
                : [adjustmentParsed])
        ]
    };
}));
export const AdjustmentParseService = {
    parseAdjustments
};
